import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import styles from './UserForm.module.css';

import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { AccountCircle, Lock, Save, IntegrationInstructions } from '@mui/icons-material';
import TextField from '@mui/material/TextField';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import LoadingButton from '@mui/lab/LoadingButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';

import UserInput from '../../../UI/user-input-hook';
import { useLocation } from 'react-router-dom';

import XeroIconPng from '../../../assets/icon/xero-icon.png'
//import { setAuthCode } from '../../../store/auth-actions';
import { useDispatch, useSelector } from 'react-redux';
import { XeroConstant } from '../../../Constants';
import { getXeroConnection } from '../../../store/xero-actions';

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <>{children}</>
            )}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
        disableRipple: true,
        style: { color: '#444' }
    };
}

const parseHash = (hash) => {
    // hash like "#xero?status=connected" or "#xero&error=invalid_grant"
    if (!hash || hash === '#') return { tab: null, params: new URLSearchParams() };
    
    const raw = hash.startsWith('#') ? hash.slice(1) : hash; // remove '#'
    const [tab, qp] = raw.split(/[?&]/, 2);                  // support ? or &
    const params = new URLSearchParams(qp || '');
    return { tab, params };
};

const messageFor = (status, error) => {
    if (status === 'connected') return { severity: 'success', message: 'Xero connected successfully.' };

    if (error === 'xero_denied') return { severity: 'warning', message: 'Connection cancelled at Xero.' };
    if (error === 'missing_code') return { severity: 'error', message: 'Missing authorization code. Please try again.' };
    if (error === 'no_tenant') return { severity: 'error', message: 'No Xero organisation was found for this account.' };
    if (error === 'capability_failed') return { severity: 'error', message: 'Your Xero account lacks required permissions for this action. You need either standard or advisor role.' };
    if (error === 'invalid_grant') return { severity: 'error', message: 'Authorization code expired or invalid. Please try again.' };
    if (error === 'client_misconfig') return { severity: 'error', message: 'Xero app configuration error. Contact support.' };
    if (error === 'rate_limited') return { severity: 'warning', message: 'Xero rate limit reached. Please try again shortly.' };
    if (error === 'callback_failed') return { severity: 'error', message: 'Could not complete Xero connection. Try again.' };

    return null;
};

const parentStyle = { flexGrow: 1, bgcolor: 'background.paper', display: 'flex' };

const UserForm = (props) => {

    const dispatch = useDispatch();

    const { user, onSave, viewOnly, isUpdating } = props;

    const location = useLocation();

    const [toast, setToast] = useState({ open: false, severity: 'info', message: '' });
    const closeToast = () => setToast(t => ({ ...t, open: false }));

    //tab value
    const [value, setValue] = useState(0);
    const handleChange = (_, newValue) => { setValue(newValue); };

    //Form values    
    const [firstName, setFirstName] = useState(user.firstname);
    const firstNameInputHasError = firstName?.trim() === ''

    const [lastName, setLastName] = useState(user.lastname);

    const [displayName, setDisplayName] = useState(user.displayname);
    const displayNameInputHasError = displayName?.trim() === ''

    const [email, setEmail] = useState(user.email);
    const emailInputHasError = email?.trim() === '' || !email?.includes('@')

    const [contact, setContact] = useState(user.contact);

    const [selectedRole, setSelectedRole] = useState(user.role);

    const xeroConn = useSelector(state => state.xero.connection);
    const xeroConnLoading = useSelector(state => state.xero.req_connection_loading);
    const xeroConnError = useSelector(state => state.xero.req_connection_error);

    const maskEmail = (e) => (e ? e.replace(/(^..).+(@.*$)/, '$1***$2') : null);

    const formatSeconds = (s) => {
        if (s == null) return '—';
        const m = Math.floor(s / 60);
        const sec = s % 60;
        if (m >= 1) return `${m}m ${sec}s`;
        return `${sec}s`;
    };

    const statusToChip = (status, aboutToExpire) => {
        if (status === 'valid' && aboutToExpire) return { label: 'EXPIRING SOON', color: 'warning' };
        if (status === 'valid') return { label: 'CONNECTED', color: 'success' };
        if (status === 'expired') return { label: 'EXPIRED', color: 'error' };
        if (status === 'revoked') return { label: 'REVOKED', color: 'error' };
        if (status === 'invalid') return { label: 'INVALID', color: 'error' };
        return { label: 'NOT CONNECTED', color: 'default' };
    };

    const {
        value: enteredPassword,
        isValid: passwordIsValid,
        hasError: passwordInputHasError,
        valueChangeHandler: passwordChangedHandler,
        inputBlurHandler: passwordBlurHandler } = UserInput(value => value !== '' && value.length >= 7);

    const {
        value: enteredCfmPassword,
        isValid: cfmPasswordIsValid,
        hasError: cfmPasswordInputHasError,
        valueChangeHandler: cfmPasswordChangedHandler,
        inputBlurHandler: cfmPasswordBlurHandler } = UserInput(value => value !== '' && value === enteredPassword);

    const _passwordInputHasError = () => {
        if (enteredPassword === '' && enteredCfmPassword === '') {
            return false;
        }

        return passwordInputHasError
    }

    const _cfmPasswordInputHasError = () => {
        if (enteredPassword === '' && enteredCfmPassword === '') {
            return false;
        }

        return cfmPasswordInputHasError
    }

    const validPassword = () => {
        if (enteredPassword === '' && enteredCfmPassword === '') {
            return true;
        }

        return passwordIsValid && cfmPasswordIsValid;
    }

    const passwordErrorText = () => {
        if (enteredPassword === '' && enteredCfmPassword === '') {
            return null;
        }

        if (!_passwordInputHasError()) {
            return null;
        }

        return enteredPassword === '' ?
            'Password cannot be empty' :
            'password must have more than 6 chars'
    }

    const cfmPasswordErrorText = () => {

        if (enteredPassword === '' && enteredCfmPassword === '') {
            return null;
        }

        if (!_cfmPasswordInputHasError()) {
            return null;
        }

        return enteredCfmPassword === '' ?
            'Password cannot be empty' :
            'password does not match'
    }

    const formIsValid = !firstNameInputHasError &&
        !displayNameInputHasError &&
        !emailInputHasError &&
        validPassword()

    //Replacing top. If works, top can remove.
    useEffect(() => {
        const { tab, params } = parseHash(location.hash);

        // If tab is xero, open the Xero tab
        if (tab === 'xero') setValue(2);

        // Check for status / error from callback
        const status = params.get('status');
        const error = params.get('error');
        const m = messageFor(status, error);

        if (m) {
            setToast({ open: true, severity: m.severity, message: m.message });

            // Remove status/error from URL but keep #xero so tab stays open
            const newUrl = `${location.pathname}#xero`;
            window.history.replaceState({}, '', newUrl);
        }
    }, [location.hash, location.pathname]);

    useEffect(() => {
        dispatch(getXeroConnection());
    }, [dispatch]);

    const handleSaveHandler = () => {
        var update = {
            firstname: firstName,
            lastname: lastName,
            displayname: displayName,
            contact,
            email,
        }

        if (!_cfmPasswordInputHasError() && enteredPassword !== '') {
            update['password'] = enteredPassword;
        }

        onSave(update)
    }

    const connectXeroHandler = () => {
        window.location.assign(
            `https://login.xero.com/identity/connect/authorize` +
            `?response_type=code` +
            `&client_id=${XeroConstant.ClientID}` +
            `&redirect_uri=${encodeURIComponent(XeroConstant.RedirectURL)}` +
            `&scope=${encodeURIComponent(XeroConstant.Scope)}`
        );
    }

    const renderXeroButton = () => {
        return <div className={styles['xero-button']} onClick={connectXeroHandler}>
            <img src={XeroIconPng} alt="xero-icon" />
            Sign In with Xero
        </div>
    }

    const renderXeroSignInPrompt = () => {
        return (
            <>
                <span>
                    Connect your Xero organisation to enable contact search and invoice lookups directly from orders.
                </span>
                <span style={{ color: '#404040', fontStyle: 'italic', marginTop: 16, fontSize: '0.8rem' }}>
                    You will be redirected to Xero to authorise access.
                </span>
                {/* {renderXeroButton()} */}
            </>
        )
    }

    const renderXeroFields = (data) => {
        const chip = statusToChip(data?.status, data?.access?.aboutToExpire);
        const expiresHuman = data?.access?.secondsRemaining != null ? formatSeconds(data.access.secondsRemaining) : '—';
        const lastRefresh = data?.audit?.lastRefreshAt ? dayjs(data.audit.lastRefreshAt).format('DD/MM/YYYY hh:mm a') : '—';
        const connectedSince = data?.audit?.connectedSince ? dayjs(data.audit.connectedSince).format('DD/MM/YYYY hh:mm a') : '—';
        
        return (
            <Paper elevation={0} sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2, width: '100%', background: '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <img src={XeroIconPng} alt="xero-icon" style={{ width: 24, height: 24 }} />
                        <h3 style={{ margin: 0 }}>Xero Integration</h3>
                    </div>
                    <Chip variant="filled" color={chip.color} label={chip.label} size="small" />
                </div>
                <Divider sx={{ my: 1.5 }} />

                <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', rowGap: 10, columnGap: 16 }}>
                    <span style={{ color: '#666' }}>Organisation</span>
                    <span>{data?.tenant?.name || '—'}</span>

                    <span style={{ color: '#666' }}>Organisation ID</span>
                    <span>{data?.tenant?.id || '—'}</span>

                    <span style={{ color: '#666' }}>Connected By</span>
                    <span>{maskEmail(data?.audit?.authorisedBy) || '—'}</span>

                    <span style={{ color: '#666' }}>Connected Since</span>
                    <span>{connectedSince}</span>

                    <span style={{ color: '#666' }}>Last Refresh</span>
                    <span>{lastRefresh}</span>

                    <span style={{ color: '#666' }}>Access Token Expiry</span>
                    <span>{data?.access?.expiresAt ? dayjs(data.access.expiresAt).format('DD/MM/YYYY hh:mm a') : '—'} ({expiresHuman})</span>
                </div>
                <Divider sx={{ my: 1.5 }} />

                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                    {data?.status !== 'valid' ? (
                        // Disconnected states → show the big Xero button
                        renderXeroButton()
                    ) : (
                        <>
                            {/* Connected chip with informative tooltip */}
                            <Tooltip
                                title={
                                    data?.access?.aboutToExpire
                                        ? 'Token will auto-refresh soon. You can also reconnect now.'
                                        : 'Connection is active'
                                }
                            >
                                <div className={styles['status-chip']} style={{ background: '#E2F0D9' }}>
                                    Connected
                                </div>
                            </Tooltip>

                            {/* If about to expire, offer a small manual reconnect */}
                            {data?.access?.aboutToExpire && (
                                <Button
                                    onClick={connectXeroHandler}
                                    size="small"
                                    variant="text"
                                    sx={{ textTransform: 'none' }}
                                >
                                    Reconnect now
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </Paper>
        )
    }

    return (
        <>
            <Box sx={parentStyle}>
                <Tabs
                    orientation="vertical"
                    value={value}
                    onChange={handleChange}
                    aria-label="profile tabs"
                    sx={{ borderRight: 1, borderColor: 'divider', flex: '0 0 auto' }}
                >
                    <Tab
                        label="Account"

                        icon={<AccountCircle fontSize='small' />}
                        iconPosition="start"
                        {...a11yProps(0)} />

                    {!viewOnly && <Tab
                        label="Password"
                        icon={<Lock fontSize='small' />}
                        iconPosition="start"
                        {...a11yProps(1)} />}

                    {
                        location.pathname === '/profile' &&
                        <Tab
                            label="Xero"
                            icon={<IntegrationInstructions fontSize='small' />}
                            iconPosition="start"
                            {...a11yProps(2)} />
                    }

                </Tabs>

                <TabPanel value={value} index={0} className={value === 0 ? styles['tab-wrapper'] : null}>
                    <h2>General Info</h2>
                    <div className={styles['input-row']}>
                        <div className={styles['input-group']}>
                            FIRST NAME
                            <TextField
                                error={firstNameInputHasError}
                                id="firstname"
                                variant="outlined"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                helperText={firstNameInputHasError ? 'Cannot be empty' : null} />
                        </div>
                        <div className={styles['input-group']}>
                            LAST NAME
                            <TextField
                                id="lastname"
                                variant="outlined"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)} />
                        </div>
                    </div>
                    <div className={styles['input-row']}>
                        <div className={styles['input-group']}>
                            DISPLAY NAME
                            <TextField
                                error={displayNameInputHasError}
                                id="displayname"
                                variant="outlined"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                helperText={displayNameInputHasError ? 'Cannot be empty' : null} />
                        </div>
                        <div className={styles['input-group']}>
                            CONTACT
                            <TextField
                                id="contact"
                                variant="outlined"
                                value={contact}
                                onChange={(e) => setContact(e.target.value)} />
                        </div>
                    </div>
                    <div className={styles['input-row']}>
                        <div className={styles['input-group']}>
                            EMAIL
                            <TextField
                                error={emailInputHasError}
                                id="email"
                                variant="outlined"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                helperText={emailInputHasError ? 'Cannot be empty' : null} />
                        </div>
                    </div>
                    {
                        (user.role !== 'Owner' && user.role !== 'SysInternal') &&
                        <div className={styles['input-row']}>
                            <div className={styles['input-group']}>
                                User Type
                                <RadioGroup
                                    aria-labelledby="role-buttons-group"
                                    name="role-buttons-group"
                                    value={selectedRole}
                                    onChange={(e) => setSelectedRole(e.target.value)}
                                >
                                    <FormControlLabel
                                        value="Manager"
                                        control={<Radio size="small" />}
                                        label={<span>Manager</span>} />

                                    <FormControlLabel
                                        value="Standard"
                                        control={<Radio size="small" />}
                                        label={<span>Standard</span>} />
                                </RadioGroup>
                            </div>
                        </div>
                    }

                </TabPanel>

                <TabPanel value={value} index={1} className={value === 1 ? styles['tab-wrapper'] : null}>
                    <h2>Change Password</h2>
                    <div className={styles['input-row']}>
                        <div className={styles['input-group']}>
                            PASSWORD
                            <TextField
                                error={_passwordInputHasError()}
                                id="password"
                                variant="outlined"
                                type="password"
                                value={enteredPassword}
                                onChange={passwordChangedHandler}
                                onBlur={passwordBlurHandler}
                                helperText={passwordErrorText()} />
                        </div>
                        <div className={styles['input-group']}>
                            CONFIRM PASSWORD
                            <TextField
                                error={_cfmPasswordInputHasError()}
                                id="cfmpassword"
                                variant="outlined"
                                type="password"
                                value={enteredCfmPassword}
                                onChange={cfmPasswordChangedHandler}
                                onBlur={cfmPasswordBlurHandler}
                                helperText={cfmPasswordErrorText()} />
                        </div>
                    </div>
                </TabPanel>

                <TabPanel value={value} index={2} className={styles['tab-wrapper']}>
                    <h2>Integration</h2>

                    {xeroConnLoading && <span>Loading Xero connection…</span>}

                    {/* Error */}
                    {!xeroConnLoading && xeroConnError && (
                        <span style={{ color: 'crimson' }}>{String(xeroConnError)}</span>
                    )}

                    {/* Data present */}
                    {!xeroConnLoading && !xeroConnError && xeroConn && (
                        <>
                            {/* If not connected, also show the sign-in prompt below the card */}
                            {xeroConn.status !== 'valid' && (
                                <div style={{ marginBottom: 12 }}>
                                    {renderXeroSignInPrompt()}
                                </div>
                            )}
                            {renderXeroFields(xeroConn)}

                        </>
                    )}
                </TabPanel>
            </Box>

            {!viewOnly && <div className={styles['action-wrapper']}>
                <LoadingButton
                    color="success"
                    onClick={handleSaveHandler}
                    loading={isUpdating}
                    loadingPosition="start"
                    startIcon={<Save />}
                    variant="contained"
                    disabled={!formIsValid}
                >
                    <span>Save</span>
                </LoadingButton>
            </div>}

            <Snackbar open={toast.open} autoHideDuration={4000} onClose={closeToast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={closeToast} severity={toast.severity} variant="filled" sx={{ width: '100%' }}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    )
}

export default UserForm